import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import {
  STATUS_LABELS,
  STATUS_COLORS,
  INCIDENT_CATEGORIES,
  INCIDENT_STATUS_COLORS,
} from '../lib/constants';
import PackageStatusBadge from '../components/PackageStatusBadge';
import ProfessionalLayout from '../components/ProfessionalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  ArrowLeft,
  Package,
  CheckCircle,
  Truck,
  AlertTriangle,
  Clock,
  BarChart3,
  Mail,
  Phone,
  MapPin,
  Calendar,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Couleur de la barre de progression selon l'avancement.
const progressBarColor = (pct) => (pct >= 80 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500');

// Libellé + couleur du statut départ.
const SHIPMENT_STATUS_META = {
  scheduled: { label: 'Planifié', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  planned: { label: 'Planifié', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  in_progress: { label: 'En cours', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  in_transit: { label: 'En transit', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  arrived: { label: 'Arrivé', cls: 'bg-green-100 text-green-700 border-green-200' },
  awaiting_delivery: { label: 'En attente livraison', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  delivering: { label: 'En livraison', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  completed: { label: 'Terminé', cls: 'bg-green-100 text-green-700 border-green-200' },
  delayed: { label: 'Retardé', cls: 'bg-red-100 text-red-700 border-red-200' },
  cancelled: { label: 'Annulé', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};
const shipmentStatusMeta = (s) => SHIPMENT_STATUS_META[s] || { label: s || '—', cls: 'bg-muted text-muted-foreground border-border' };

const getInitials = (name, email) => {
  if (name) {
    const names = name.split(' ');
    if (names.length >= 2) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }
  return email ? email.substring(0, 2).toUpperCase() : 'U';
};

const formatDate = (dateStr, lang = 'fr') => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

export default function DeliveryPartnerActivity() {
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shipmentFilter, setShipmentFilter] = useState('all');

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/api/admin/delivery-partners/${partnerId}/activity`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.data?.detail || err.userMessage || (language === 'en' ? 'Error loading data' : 'Erreur lors du chargement'));
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [partnerId]);

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="space-y-6 p-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-28" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </ProfessionalLayout>
    );
  }

  if (error) {
    return (
      <ProfessionalLayout>
        <div className="p-6 space-y-4">
          <Button variant="ghost" onClick={() => navigate('/team')}>
            <ArrowLeft size={16} className="mr-2" />
            {language === 'en' ? 'Back' : 'Retour'}
          </Button>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </ProfessionalLayout>
    );
  }

  if (!data) return null;

  const { partner, kpis, recent_deliveries, incidents, shipments, undelivered_packages = [] } = data;

  return (
    <ProfessionalLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/team')}>
            <ArrowLeft size={16} />
          </Button>
          <div className="flex items-center gap-4 flex-1">
            <Avatar className="w-12 h-12 bg-amber-500">
              <AvatarFallback className="bg-amber-500 text-white font-bold text-lg">
                {getInitials(partner.name, partner.email)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-xl font-bold">{partner.name || partner.email}</h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {partner.email && (
                  <span className="flex items-center gap-1">
                    <Mail size={13} /> {partner.email}
                  </span>
                )}
                {partner.phone && (
                  <span className="flex items-center gap-1">
                    <Phone size={13} /> {partner.phone}
                  </span>
                )}
                {(partner.assigned_countries?.length > 0 || partner.assigned_cities?.length > 0) && (
                  <span className="flex items-center gap-1">
                    <MapPin size={13} />
                    {[
                      ...(partner.assigned_countries || []),
                      ...(partner.assigned_cities || []),
                    ].join(', ')}
                  </span>
                )}
                {partner.created_at && (
                  <span className="flex items-center gap-1">
                    <Calendar size={13} /> {language === 'en' ? 'Joined' : 'Inscrit'} {formatDate(partner.created_at, language)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Delivered Packages' : 'Colis Livres'}</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.total_delivered}</div>
              <p className="text-xs text-muted-foreground">{language === 'en' ? 'Validated deliveries' : 'Livraisons validees'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Assigned Packages' : 'Colis Assignes'}</CardTitle>
              <Package className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.total_assigned}</div>
              <p className="text-xs text-muted-foreground">{language === 'en' ? 'Total in their departures' : 'Total dans ses departs'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Delivery Rate' : 'Taux Livraison'}</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.delivery_rate}%</div>
              <Progress value={kpis.delivery_rate} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Active Departures' : 'Departs Actifs'}</CardTitle>
              <Truck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.active_shipments}</div>
              <p className="text-xs text-muted-foreground">{language === 'en' ? 'In progress / arrived' : 'En cours / arrivees'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{language === 'en' ? 'Open Incidents' : 'Incidents Ouverts'}</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpis.open_incidents}</div>
              {kpis.avg_delivery_time_hours > 0 && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock size={11} /> {language === 'en' ? 'Avg.' : 'Moy.'} {kpis.avg_delivery_time_hours}h
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="deliveries">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="deliveries">
              {language === 'en' ? 'Deliveries' : 'Livraisons'} ({recent_deliveries.length})
            </TabsTrigger>
            <TabsTrigger value="incidents">
              {language === 'en' ? 'Incidents' : 'Incidents'} ({incidents.length})
            </TabsTrigger>
            <TabsTrigger value="shipments">
              {language === 'en' ? 'Departures' : 'Departs'} ({shipments.length})
            </TabsTrigger>
            <TabsTrigger value="performances">
              {language === 'en' ? 'Performance' : 'Performances'}
            </TabsTrigger>
          </TabsList>

          {/* Deliveries Tab */}
          <TabsContent value="deliveries">
            <Card>
              <CardContent className="p-0">
                {recent_deliveries.length === 0 ? (
                  <p className="text-muted-foreground text-sm p-6">{language === 'en' ? 'No deliveries recorded.' : 'Aucune livraison enregistree.'}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'OCE Code' : 'Code OCE'}</TableHead>
                        <TableHead>{t('recipient')}</TableHead>
                        <TableHead>{language === 'en' ? 'Delivery Date' : 'Date Livraison'}</TableHead>
                        <TableHead>{language === 'en' ? 'Status' : 'Statut'}</TableHead>
                        <TableHead>{language === 'en' ? 'Departure Ref.' : 'Ref. Depart'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recent_deliveries.map((d, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-mono text-sm">{d.oce_code || '—'}</TableCell>
                          <TableCell>{d.recipient_name || '—'}</TableCell>
                          <TableCell className="text-sm">{formatDate(d.delivered_at, language)}</TableCell>
                          <TableCell>
                            <PackageStatusBadge status={d.status} size="sm" />
                          </TableCell>
                          <TableCell className="text-sm">{d.shipment_reference || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents">
            <Card>
              <CardContent className="p-0">
                {incidents.length === 0 ? (
                  <p className="text-muted-foreground text-sm p-6">{language === 'en' ? 'No incidents reported.' : 'Aucun incident signale.'}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'Category' : 'Categorie'}</TableHead>
                        <TableHead>{language === 'en' ? 'Status' : 'Statut'}</TableHead>
                        <TableHead>{language === 'en' ? 'OCE Code' : 'Code OCE'}</TableHead>
                        <TableHead>{language === 'en' ? 'Date' : 'Date'}</TableHead>
                        <TableHead>{language === 'en' ? 'Resolution Notes' : 'Notes Resolution'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {incidents.map((inc) => (
                        <TableRow key={inc.id}>
                          <TableCell>
                            {(INCIDENT_CATEGORIES[language] || INCIDENT_CATEGORIES.fr)[inc.category] || inc.category}
                          </TableCell>
                          <TableCell>
                            <Badge className={`${INCIDENT_STATUS_COLORS[inc.status] || ''} text-xs`}>
                              {inc.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{inc.package_oce_code || '—'}</TableCell>
                          <TableCell className="text-sm">{formatDate(inc.created_at, language)}</TableCell>
                          <TableCell className="text-sm max-w-[120px] sm:max-w-[200px] truncate">
                            {inc.resolution_note || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Shipments Tab */}
          <TabsContent value="shipments">
            {/* Filtre par statut */}
            <div className="flex flex-wrap gap-2 mb-4">
              {['all', ...Array.from(new Set(shipments.map(s => s.status)))].map(st => (
                <button
                  key={st}
                  onClick={() => setShipmentFilter(st)}
                  className={`px-3 py-1.5 rounded-full text-xs border ${shipmentFilter === st ? 'bg-orange-500 text-white border-orange-500' : 'bg-background hover:bg-muted border-border'}`}
                >
                  {st === 'all' ? (language === 'en' ? 'All' : 'Tous') : shipmentStatusMeta(st).label}
                  {' '}({st === 'all' ? shipments.length : shipments.filter(s => s.status === st).length})
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {shipments.length === 0 ? (
                <p className="text-muted-foreground text-sm p-6">{language === 'en' ? 'No departures.' : 'Aucun depart.'}</p>
              ) : (
                shipments
                  .filter(s => shipmentFilter === 'all' || s.status === shipmentFilter)
                  .map((s) => {
                    const meta = shipmentStatusMeta(s.status);
                    return (
                      <Card key={s.id}>
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <CardTitle className="text-sm font-semibold truncate">{s.name || s.reference}</CardTitle>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {s.route}{s.flight_name ? ` · ${s.flight_name}` : ''}
                                {s.departure_date ? ` · ${formatDate(s.departure_date, language)}` : ''}
                              </p>
                            </div>
                            <Badge variant="outline" className={`text-xs shrink-0 ${meta.cls}`}>{meta.label}</Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">{language === 'en' ? 'Progress' : 'Progression'}</span>
                            <span className="font-medium">
                              {s.delivered_packages}/{s.total_packages} ({s.delivery_progress}%)
                            </span>
                          </div>
                          <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                            <div className={`h-full rounded-full ${progressBarColor(s.delivery_progress)}`} style={{ width: `${s.delivery_progress}%` }} />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
              )}
            </div>
          </TabsContent>

          {/* Performances Tab */}
          <TabsContent value="performances" className="space-y-6">
            {/* Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{language === 'en' ? 'Delivery Rate' : 'Taux Livraison'}</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.delivery_rate}%</div>
                  <Progress value={kpis.delivery_rate} className="mt-2" />
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{language === 'en' ? 'Average Time' : 'Temps Moyen'}</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {kpis.avg_delivery_time_hours > 0 ? `${kpis.avg_delivery_time_hours}h` : '—'}
                  </div>
                  <p className="text-xs text-muted-foreground">{language === 'en' ? 'Per delivery' : 'Par livraison'}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{language === 'en' ? 'Incident Rate' : 'Taux Incidents'}</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.incident_rate || 0}%</div>
                  <p className="text-xs text-muted-foreground">{kpis.total_incidents || 0} incidents / {kpis.total_assigned} {language === 'en' ? 'packages' : 'colis'}</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{language === 'en' ? 'To Deliver' : 'A Livrer'}</CardTitle>
                  <Package className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{kpis.undelivered_count || 0}</div>
                  <p className="text-xs text-muted-foreground">{language === 'en' ? 'Cleared, awaiting' : 'Dedouanes, en attente'}</p>
                </CardContent>
              </Card>
            </div>

            {/* Progression par depart */}
            {shipments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{language === 'en' ? 'Progress by Departure' : 'Progression par Depart'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {shipments.map((s) => (
                    <div key={s.id} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium truncate">{s.name || s.reference}{s.route ? ` — ${s.route}` : ''}</span>
                        <span className="text-muted-foreground shrink-0">
                          {s.delivered_packages}/{s.total_packages} ({s.delivery_progress}%)
                        </span>
                      </div>
                      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                        <div className={`h-full rounded-full ${progressBarColor(s.delivery_progress)}`} style={{ width: `${s.delivery_progress}%` }} />
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Colis Non Livres */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg">{language === 'en' ? 'Undelivered Packages' : 'Colis Non Livres'}</CardTitle>
                  {undelivered_packages.length > 0 && (
                    <Badge variant="destructive" className="text-xs">
                      {undelivered_packages.length}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {undelivered_packages.length === 0 ? (
                  <p className="text-muted-foreground text-sm p-6">{language === 'en' ? 'No packages awaiting delivery.' : 'Aucun colis en attente de livraison.'}</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{language === 'en' ? 'OCE Code' : 'Code OCE'}</TableHead>
                        <TableHead>{t('recipient')}</TableHead>
                        <TableHead>{language === 'en' ? 'Destination' : 'Destination'}</TableHead>
                        <TableHead>{language === 'en' ? 'Cleared since' : 'Dedouane depuis'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {undelivered_packages.map((pkg) => (
                        <TableRow key={pkg.id}>
                          <TableCell className="font-mono text-sm">{pkg.oce_code || '—'}</TableCell>
                          <TableCell>{pkg.recipient_name || '—'}</TableCell>
                          <TableCell>
                            {[pkg.destination_city, pkg.destination_country].filter(Boolean).join(', ') || '—'}
                          </TableCell>
                          <TableCell className="text-sm">{formatDate(pkg.updated_at, language)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </ProfessionalLayout>
  );
}
